/**
 * Design Verification Items List API Service
 *
 * Handles all verification-related API calls including:
 * - Session management (CRUD)
 * - Unit management
 * - Item management
 * - Excel export
 */

import { api } from './api';
import {
  VerificationSession,
  VerificationUnit,
  VerificationItem,
  SessionListResponse,
} from '../types/verification';

// ==============================================================================
// API Base URL
// ==============================================================================

const VERIFICATION_BASE_URL = '/verification';

// ==============================================================================
// Helper: Map snake_case API response to camelCase
// ==============================================================================

const mapItem = (raw: any): VerificationItem => ({
  id: raw.id,
  sessionId: raw.session_id,
  unitId: raw.unit_id,
  sortOrder: raw.sort_order,
  specification: raw.specification || '',
  itemName: raw.item_name || '',
  classifications: raw.classifications || [],
  standardTarget: raw.standard_target || '',
  verificationMethodsPrimary: raw.verification_methods_primary || [],
  verificationMethodsSecondary: raw.verification_methods_secondary || [],
  qualityRecordNo: raw.quality_record_no || '',
  qualityRecordTitle: raw.quality_record_title || '',
  reviewJudgment: raw.review_judgment,
  qualityRecordNoRight: raw.quality_record_no_right || '',
  qualityRecordTitleRight: raw.quality_record_title_right || '',
  sourceDrbfmSessionId: raw.source_drbfm_session_id,
  sourceFailureModeId: raw.source_failure_mode_id,
  createdAt: raw.created_at,
  updatedAt: raw.updated_at,
});

const mapUnit = (raw: any): VerificationUnit => ({
  id: raw.id,
  sessionId: raw.session_id,
  unitNumber: raw.unit_number || '',
  unitName: raw.unit_name || '',
  sortOrder: raw.sort_order,
  items: (raw.items || []).map(mapItem),
  createdAt: raw.created_at,
});

const mapSession = (raw: any): VerificationSession => ({
  id: raw.id,
  tenantId: raw.tenant_id,
  userId: raw.user_id,
  title: raw.title,
  modelName: raw.model_name,
  description: raw.description,
  units: (raw.units || []).map(mapUnit),
  createdAt: raw.created_at,
  updatedAt: raw.updated_at,
});

// ==============================================================================
// Session Management
// ==============================================================================

export const createSession = async (params: {
  title: string;
  modelName?: string;
  description?: string;
}): Promise<VerificationSession> => {
  const response = await api.post<any>(`${VERIFICATION_BASE_URL}/sessions`, {
    title: params.title,
    model_name: params.modelName,
    description: params.description,
  });
  return mapSession(response);
};

export const listSessions = async (params: {
  limit?: number;
  offset?: number;
} = {}): Promise<SessionListResponse> => {
  const { limit = 20, offset = 0 } = params;
  const response = await api.get<any>(
    `${VERIFICATION_BASE_URL}/sessions?limit=${limit}&offset=${offset}`
  );
  return {
    sessions: (response.sessions || []).map((s: any) => mapSession(s)),
    total: response.total,
    limit: response.limit,
    offset: response.offset,
  };
};

export const getSession = async (sessionId: string): Promise<VerificationSession> => {
  const response = await api.get<any>(`${VERIFICATION_BASE_URL}/sessions/${sessionId}`);
  return mapSession(response);
};

export const updateSession = async (
  sessionId: string,
  params: { title?: string; modelName?: string; description?: string }
): Promise<VerificationSession> => {
  const response = await api.put<any>(`${VERIFICATION_BASE_URL}/sessions/${sessionId}`, {
    title: params.title,
    model_name: params.modelName,
    description: params.description,
  });
  return mapSession(response);
};

export const deleteSession = async (sessionId: string): Promise<void> => {
  await api.delete(`${VERIFICATION_BASE_URL}/sessions/${sessionId}`);
};

// ==============================================================================
// Unit Management
// ==============================================================================

export const addUnit = async (
  sessionId: string,
  params: { unitNumber: string; unitName: string; sortOrder?: number }
): Promise<VerificationUnit> => {
  const response = await api.post<any>(
    `${VERIFICATION_BASE_URL}/sessions/${sessionId}/units`,
    {
      unit_number: params.unitNumber,
      unit_name: params.unitName,
      sort_order: params.sortOrder || 0,
    }
  );
  return mapUnit(response);
};

export const updateUnit = async (
  sessionId: string,
  unitId: string,
  params: { unitNumber: string; unitName: string; sortOrder?: number }
): Promise<VerificationUnit> => {
  const response = await api.put<any>(
    `${VERIFICATION_BASE_URL}/sessions/${sessionId}/units/${unitId}`,
    {
      unit_number: params.unitNumber,
      unit_name: params.unitName,
      sort_order: params.sortOrder || 0,
    }
  );
  return mapUnit(response);
};

export const deleteUnit = async (sessionId: string, unitId: string): Promise<void> => {
  await api.delete(`${VERIFICATION_BASE_URL}/sessions/${sessionId}/units/${unitId}`);
};

// ==============================================================================
// Item Management
// ==============================================================================

export const addItem = async (
  sessionId: string,
  params: {
    unitId: string;
    specification?: string;
    itemName?: string;
    classifications?: string[];
    standardTarget?: string;
    verificationMethodsPrimary?: string[];
    verificationMethodsSecondary?: string[];
    qualityRecordNo?: string;
    qualityRecordTitle?: string;
    reviewJudgment?: string | null;
    qualityRecordNoRight?: string;
    qualityRecordTitleRight?: string;
    sortOrder?: number;
  }
): Promise<VerificationItem> => {
  const response = await api.post<any>(
    `${VERIFICATION_BASE_URL}/sessions/${sessionId}/items`,
    {
      unit_id: params.unitId,
      specification: params.specification || '',
      item_name: params.itemName || '',
      classifications: params.classifications || [],
      standard_target: params.standardTarget || '',
      verification_methods_primary: params.verificationMethodsPrimary || [],
      verification_methods_secondary: params.verificationMethodsSecondary || [],
      quality_record_no: params.qualityRecordNo || '',
      quality_record_title: params.qualityRecordTitle || '',
      review_judgment: params.reviewJudgment,
      quality_record_no_right: params.qualityRecordNoRight || '',
      quality_record_title_right: params.qualityRecordTitleRight || '',
      sort_order: params.sortOrder || 0,
    }
  );
  return mapItem(response);
};

export const updateItem = async (
  sessionId: string,
  itemId: string,
  params: {
    specification?: string;
    itemName?: string;
    classifications?: string[];
    standardTarget?: string;
    verificationMethodsPrimary?: string[];
    verificationMethodsSecondary?: string[];
    qualityRecordNo?: string;
    qualityRecordTitle?: string;
    reviewJudgment?: string | null;
    qualityRecordNoRight?: string;
    qualityRecordTitleRight?: string;
    sortOrder?: number;
  }
): Promise<VerificationItem> => {
  const request: any = {};
  if (params.specification !== undefined) request.specification = params.specification;
  if (params.itemName !== undefined) request.item_name = params.itemName;
  if (params.classifications !== undefined) request.classifications = params.classifications;
  if (params.standardTarget !== undefined) request.standard_target = params.standardTarget;
  if (params.verificationMethodsPrimary !== undefined)
    request.verification_methods_primary = params.verificationMethodsPrimary;
  if (params.verificationMethodsSecondary !== undefined)
    request.verification_methods_secondary = params.verificationMethodsSecondary;
  if (params.qualityRecordNo !== undefined) request.quality_record_no = params.qualityRecordNo;
  if (params.qualityRecordTitle !== undefined) request.quality_record_title = params.qualityRecordTitle;
  if (params.reviewJudgment !== undefined) request.review_judgment = params.reviewJudgment;
  if (params.qualityRecordNoRight !== undefined)
    request.quality_record_no_right = params.qualityRecordNoRight;
  if (params.qualityRecordTitleRight !== undefined)
    request.quality_record_title_right = params.qualityRecordTitleRight;
  if (params.sortOrder !== undefined) request.sort_order = params.sortOrder;

  const response = await api.put<any>(
    `${VERIFICATION_BASE_URL}/sessions/${sessionId}/items/${itemId}`,
    request
  );
  return mapItem(response);
};

export const deleteItem = async (sessionId: string, itemId: string): Promise<void> => {
  await api.delete(`${VERIFICATION_BASE_URL}/sessions/${sessionId}/items/${itemId}`);
};

export const bulkAddItems = async (
  sessionId: string,
  items: Array<{
    unitId: string;
    specification?: string;
    itemName?: string;
    classifications?: string[];
    standardTarget?: string;
    verificationMethodsPrimary?: string[];
    verificationMethodsSecondary?: string[];
    qualityRecordNo?: string;
    qualityRecordTitle?: string;
    reviewJudgment?: string | null;
    qualityRecordNoRight?: string;
    qualityRecordTitleRight?: string;
    sortOrder?: number;
  }>
): Promise<{ items: VerificationItem[]; count: number }> => {
  const response = await api.post<any>(
    `${VERIFICATION_BASE_URL}/sessions/${sessionId}/items/bulk`,
    {
      items: items.map((item) => ({
        unit_id: item.unitId,
        specification: item.specification || '',
        item_name: item.itemName || '',
        classifications: item.classifications || [],
        standard_target: item.standardTarget || '',
        verification_methods_primary: item.verificationMethodsPrimary || [],
        verification_methods_secondary: item.verificationMethodsSecondary || [],
        quality_record_no: item.qualityRecordNo || '',
        quality_record_title: item.qualityRecordTitle || '',
        review_judgment: item.reviewJudgment,
        quality_record_no_right: item.qualityRecordNoRight || '',
        quality_record_title_right: item.qualityRecordTitleRight || '',
        sort_order: item.sortOrder || 0,
      })),
    }
  );
  return {
    items: (response.items || []).map(mapItem),
    count: response.count,
  };
};

// ==============================================================================
// Excel Export
// ==============================================================================

export const exportSessionAsExcel = async (sessionId: string): Promise<Blob> => {
  const response = await api.get<Blob>(
    `${VERIFICATION_BASE_URL}/sessions/${sessionId}/export/excel`,
    { responseType: 'blob' }
  );
  return response;
};

export const downloadExportedExcel = async (
  sessionId: string,
  filename?: string
): Promise<void> => {
  const blob = await exportSessionAsExcel(sessionId);

  let safeFilename = filename || `verification_${sessionId.slice(0, 8)}.xlsx`;
  if (!safeFilename.toLowerCase().endsWith('.xlsx')) {
    safeFilename = `${safeFilename}.xlsx`;
  }
  safeFilename = safeFilename.replace(/[<>:"/\\|?*]/g, '_');

  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = safeFilename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, 1000);
};

// ==============================================================================
// Service Object Export
// ==============================================================================

export const verificationService = {
  createSession,
  listSessions,
  getSession,
  updateSession,
  deleteSession,
  addUnit,
  updateUnit,
  deleteUnit,
  addItem,
  updateItem,
  deleteItem,
  bulkAddItems,
  exportSessionAsExcel,
  downloadExportedExcel,
};

export default verificationService;
